import React, { useState } from "react";
import { IconServiceType } from "../IconServiceType";
import { DisconnectionOrderDetailsForm } from "./_/DisconnectionOrderDetailsForm";
import { Form, Formik } from "formik";
import { DisconnectionOrderDetailsFormSchema } from "./DisconnectionOrderDetailsFormSchema";
import { DisconnectionFormValues } from "../../types/DisconnectionFormValues";
import styled from "styled-components";
import { space, SpaceProps } from "styled-system";
import { Button } from "../Button";
import { Flex } from "../Flex";
import {
  handleScrollToId,
  scrollToTop,
} from "../../utils/handleScroll/HandleScroll";
import { DISCONNECTION_FORM_STEPS } from "../DisconnectionProgressStepper/DisconnectionProgressStepper";
import { DisconnectionReviewForm } from "./_/DisconnectionReviewForm";
import { useUserAuthentication } from "../../contexts/UserAuthenticationProvider/UserAuthenticationProvider";
import { SomethingWentWrong } from "../SomethingWentWrong/SomethingWentWrong";
import { mediaQueries } from "../../theme";

interface Props {
  onStepChange: (activeStep: number) => void;
  activeStep: number;
  initialValues: DisconnectionFormValues;
  handleSave: (values: DisconnectionFormValues) => void;
  onSubmit: (values: DisconnectionFormValues) => void;
}

const validationSchemas = [
  undefined,
  DisconnectionOrderDetailsFormSchema,
  undefined,
];

const FormWrapper = styled(Form)<SpaceProps>`
  ${space}
`;

const FlexWrapper = styled(Flex)`
  ${mediaQueries.extraLargeDevice} {
    grid-column-start: 2;
  }
`;

export const DisconnectionForm: React.FC<Props> = ({
  initialValues,
  onStepChange,
  activeStep,
  handleSave,
  onSubmit,
}) => {
  const [selectedTermsConditions, setSelectedTermsConditions] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const {
    authenticatedUserContext: { isLoggedIn },
  } = useUserAuthentication();

  const isLastStep = activeStep === DISCONNECTION_FORM_STEPS.length;
  const isSubmissionErrorStep =
    activeStep === DISCONNECTION_FORM_STEPS.length + 1;

  const submitForm = (values: DisconnectionFormValues) => {
    setShowLoading(true);
    onSubmit(values);

    setTimeout(() => {
      setShowLoading(false);
    }, 3000);
  };

  const handleOnSubmit = (values: DisconnectionFormValues) => {
    if (isLastStep || isSubmissionErrorStep) {
      submitForm(values);
    } else {
      const nextStep = activeStep + 1;
      onStepChange(nextStep);
      scrollToTop();
    }
  };

  const handleBack = () => {
    const nextStep = activeStep - 1;
    onStepChange(nextStep);
  };

  const handleReviewEdit = (elementId: string) => {
    const nextStep = activeStep - 1;
    onStepChange(nextStep);
    handleScrollToId(elementId);
  };

  const handleOnSelectTermsConditions = (checked: boolean) => {
    setSelectedTermsConditions(checked);
  };

  return (
    <>
      {activeStep < 4 && (
        <IconServiceType
          title="Electricity disconnection"
          icon="flash_on"
          mt={[5, 5, 5, 5, 7]}
          mb={5}
        />
      )}

      <Formik
        validateOnChange={false}
        /* eslint-disable  @typescript-eslint/no-non-null-assertion */
        initialValues={initialValues!}
        validationSchema={validationSchemas[activeStep - 1]}
        onSubmit={handleOnSubmit}
      >
        {({ values, validateField }) => (
          <FormWrapper mb={8}>
            {activeStep === 2 && (
              <DisconnectionOrderDetailsForm
                id="disconnection-order-details-form"
                values={values}
                validateField={validateField}
              />
            )}

            {activeStep === 3 && (
              <DisconnectionReviewForm
                values={values}
                onEdit={handleReviewEdit}
                onSelectTermsConditions={handleOnSelectTermsConditions}
              />
            )}

            <Flex
              justifyContent={activeStep > 2 ? "space-between" : "flex-end"}
              mt={5}
            >
              {activeStep === 3 && (
                <Button
                  variant="secondary"
                  alignSelf="flex-start"
                  onClick={handleBack}
                >
                  Back
                </Button>
              )}

              {activeStep < 4 && (
                <Button
                  variant="secondary"
                  alignSelf="flex-start"
                  marginLeft="auto"
                  disabled={!isLoggedIn}
                  onClick={() => handleSave(values)}
                  mr="3"
                  px="6"
                  py="3"
                  data-testid={"save-button"}
                >
                  Save
                </Button>
              )}

              {activeStep < 4 && (
                <Button
                  type="submit"
                  disabled={
                    (isLastStep && !selectedTermsConditions) || showLoading
                  }
                  alignSelf="flex-end"
                  loading={showLoading}
                  data-testid={isLastStep ? "submit-button" : "next-button"}
                  px="6"
                  py="3"
                >
                  {isLastStep ? "Submit" : "Next"}
                </Button>
              )}
            </Flex>

            {activeStep === 4 && (
              <FlexWrapper flexDirection="column">
                <SomethingWentWrong
                  subHeading="It looks like there was an error submitting your order."
                  message="Please try again in a few minutes"
                  onBackClick={handleBack}
                  onTryAgainClick={() => submitForm(values)}
                  tryAgainLoading={showLoading}
                  showTryAgainButton
                  showBackButton
                />
              </FlexWrapper>
            )}
          </FormWrapper>
        )}
      </Formik>
    </>
  );
};
